import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Image as ImageIcon, Search, Plus, Loader2, AlertCircle } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';

const GalleryWidget = () => {
  const { user } = useAuth();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    fetchMyImages();

    const handleVisible = () => {
      if (document.visibilityState === 'visible') fetchMyImages();
    };
    document.addEventListener('visibilitychange', handleVisible);
    return () => document.removeEventListener('visibilitychange', handleVisible);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchMyImages = async () => {
    try {
      setLoading(true);
      setErrorMsg(null);

      const { data, error } = await supabase
        .from('prompts')
        .select('id, title, image_url, category')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(4);

      if (error) throw error;
      setImages(data || []);

    } catch (error) {
      console.error('Error cargando galería:', error.message);
      setErrorMsg(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="flex flex-col h-full bg-white/75 backdrop-blur-md border border-white/90 shadow-[0_4px_20px_rgba(99,102,241,0.06)] dark:bg-[#111114] dark:border-[#1e1e24] dark:shadow-none hover:shadow-[0_8px_30px_rgba(99,102,241,0.1)] dark:hover:shadow-none transition-all duration-300 overflow-hidden rounded-2xl">
      {/* Header */}
      <div className="flex items-center justify-between p-5 pb-4 border-b border-indigo-50/50 dark:border-[#1a1a20]">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[rgba(99,102,241,0.08)] dark:bg-[rgba(167,139,250,0.1)] flex items-center justify-center">
            <ImageIcon size={18} className="text-indigo-500 dark:text-violet-400" />
          </div>
          <h3 className="text-[#1a1625] dark:text-[#e5e5e5] font-semibold">Galería MUSA</h3>
        </div>
        <div className="flex gap-2">
          <button className="text-indigo-300 hover:text-indigo-500 dark:text-[#555] dark:hover:text-violet-400 transition-colors">
            <Search size={16} />
          </button>
          <button className="text-indigo-300 hover:text-indigo-500 dark:text-[#555] dark:hover:text-violet-400 transition-colors">
            <Plus size={16} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-5 pt-3 flex-1 flex flex-col justify-start">
        {loading ? (
          <div className="flex flex-col items-center justify-center text-[#9ca3af] dark:text-[#555] h-full py-8">
            <Loader2 className="w-6 h-6 animate-spin mb-2 text-indigo-300 dark:text-[#333]" />
            <span className="text-xs">Sincronizando con MUSA...</span>
          </div>
        ) : errorMsg ? (
          <div className="flex flex-col items-center justify-center text-red-400/80 h-full p-4 text-center bg-red-50 dark:bg-red-500/5 rounded-xl border border-red-100 dark:border-red-500/10">
            <AlertCircle className="w-6 h-6 mb-2" />
            <span className="text-xs font-medium">Error: {errorMsg}</span>
          </div>
        ) : images.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center py-8">
            <ImageIcon className="w-8 h-8 mb-2 text-indigo-200 dark:text-[#333]" />
            <span className="text-sm font-medium text-[#6b7280] dark:text-[#888] mb-1">Galería vacía</span>
            <span className="text-xs text-[#9ca3af] dark:text-[#555]">Tus creaciones aparecerán aquí.</span>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2 mt-2">
            {images.map((item) => (
              <div
                key={item.id}
                className="group relative aspect-square rounded-xl overflow-hidden bg-indigo-50/50 dark:bg-black/50 border border-indigo-50 dark:border-white/5 cursor-pointer"
              >
                {item.image_url ? (
                  <img
                    src={item.image_url}
                    alt={item.title}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-indigo-200 dark:text-[#333]">
                    <ImageIcon size={20} />
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-2">
                  <span className="text-[10px] text-white font-medium truncate">{item.title}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
};

export default GalleryWidget;
